import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  PlusCircle,
  Newspaper,
  Pencil,
  Trash2,
  CheckCircle2,
  EyeOff,
  ExternalLink,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "../../components/ui/sonner";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/blog");
      setPosts(r.data.items || []);
    } catch {
      toast.error("Kunde inte hämta inlägg");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Ta bort inlägget? Detta går inte att ångra.")) return;
    try {
      await api.delete(`/admin/blog/${id}`);
      setPosts((arr) => arr.filter((p) => p.id !== id));
      toast.success("Inlägget togs bort");
    } catch {
      toast.error("Kunde inte ta bort inlägget");
    }
  };

  return (
    <div data-testid="admin-blog-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-[#1B2A1E]">
            Blogg
          </h1>
          <p className="text-[#5A6258] mt-1 text-sm">
            {posts.length} inlägg totalt
          </p>
        </div>
        <Link
          to="/admin/blogg/nytt"
          data-testid="admin-blog-new"
          className="inline-flex items-center gap-2 bg-[#D95D2E] hover:bg-[#B84B22] text-white font-semibold px-4 py-2.5 rounded-lg transition-colors"
        >
          <PlusCircle className="w-4 h-4" /> Nytt inlägg
        </Link>
      </div>

      {loading ? (
        <div className="text-[#5A6258] p-4">Laddar…</div>
      ) : posts.length === 0 ? (
        <div
          data-testid="admin-blog-empty"
          className="bg-white border border-[#E2E0D8] rounded-xl p-10 text-center"
        >
          <Newspaper className="w-10 h-10 mx-auto mb-3 text-[#D95D2E]" />
          <h2 className="font-heading font-bold text-lg text-[#1B2A1E]">
            Inga inlägg ännu
          </h2>
          <p className="text-[#5A6258] text-sm mt-1">
            Skapa ditt första blogginlägg för att komma igång.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {posts.map((p) => (
            <li
              key={p.id}
              data-testid={`admin-blog-item-${p.id}`}
              className="bg-white border border-[#E2E0D8] rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row gap-4 sm:items-center"
            >
              <div className="w-full sm:w-32 aspect-[16/10] rounded-lg overflow-hidden bg-[#1B2A1E] shrink-0 relative">
                {p.image ? (
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Hide broken image icon, replace with soft placeholder.
                      e.currentTarget.style.display = "none";
                      const ph = e.currentTarget.nextSibling;
                      if (ph) ph.style.display = "block";
                    }}
                  />
                ) : null}
                <div
                  data-testid={`admin-blog-image-placeholder-${p.id}`}
                  className="absolute inset-0 bg-gradient-to-br from-[#1B2A1E] to-[#283224]"
                  style={{ display: p.image ? "none" : "block" }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.14em] ${
                      p.status === "published"
                        ? "bg-[#1B2A1E] text-white"
                        : "bg-[#EFEBDF] text-[#5A6258]"
                    }`}
                  >
                    {p.status === "published" ? (
                      <CheckCircle2 className="w-3 h-3" />
                    ) : (
                      <EyeOff className="w-3 h-3" />
                    )}
                    {p.status === "published" ? "Publicerad" : "Utkast"}
                  </span>
                  <span className="text-xs text-[#5A6258]">
                    {formatDate(p.published_at || p.created_at)}
                  </span>
                </div>
                <h3 className="font-heading font-bold text-[#1B2A1E] text-lg leading-snug">
                  {p.title}
                </h3>
                {p.excerpt && (
                  <p className="text-sm text-[#3a4338] mt-1 line-clamp-2">
                    {p.excerpt}
                  </p>
                )}
              </div>
              <div className="flex sm:flex-col gap-2 sm:items-end">
                {p.status === "published" && (
                  <Link
                    to={`/blogg/${p.slug}`}
                    target="_blank"
                    className="inline-flex items-center gap-1.5 text-sm text-[#3a4338] hover:text-[#D95D2E] font-semibold"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Visa
                  </Link>
                )}
                <Link
                  to={`/admin/blogg/${p.id}`}
                  data-testid={`admin-blog-edit-${p.id}`}
                  className="inline-flex items-center gap-1.5 bg-[#1B2A1E] hover:bg-[#283224] text-white text-sm font-semibold px-3 py-2 rounded-md"
                >
                  <Pencil className="w-3.5 h-3.5" /> Redigera
                </Link>
                <button
                  onClick={() => remove(p.id)}
                  data-testid={`admin-blog-delete-${p.id}`}
                  className="inline-flex items-center gap-1.5 bg-white border border-[#E2E0D8] hover:border-red-500 hover:text-red-600 text-sm font-semibold px-3 py-2 rounded-md"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Radera
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
